import { useState } from "react";
import { Bug, Heart, ImagePlus, Lightbulb, Star, X } from "lucide-react";
import { apiFetch } from "../../data/session";

const FIELDS = [
  ["bug", "Un bug ?", Bug, "Décrivez ce qui ne va pas…"],
  ["suggestion", "Suggestion de fonctionnalité ?", Lightbulb, "Qu’est-ce qui améliorerait Plan Étude pour vous ?"],
  ["other", "Autre chose ?", Heart, "Vos pensées, encouragements, ou n’importe quoi…"],
];

/** « Envoyer un commentaire » from the ☰ menu (beta). */
export const FeedbackDialog = ({ token, context = "", onClose }) => {
  const [rating, setRating] = useState(0);
  const [form, setForm] = useState({ bug: "", suggestion: "", other: "" });
  const hasText = Object.values(form).some((value) => value.trim());
  const [images, setImages] = useState([]);
  const [state, setState] = useState("edit");
  const [error, setError] = useState("");
  const addImages = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = "";
    const fits = picked.filter((file) => file.size <= 10 * 1024 * 1024);
    const room = 3 - images.length;
    const notes = [];
    if (fits.length < picked.length) notes.push("Image trop lourde (10 Mo maximum), elle n’a pas été ajoutée.");
    if (fits.length > room) notes.push(`Maximum 3 images : ${fits.length - room} non ajoutée${fits.length - room > 1 ? "s" : ""}.`);
    setError(notes.join(" "));
    return setImages((current) => [...current, ...fits.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) }))]);
  };
  const removeImage = (index) => setImages((current) => current.filter((_, i) => i !== index));
  const upload = async ({ file }) => {
    const body = new FormData();
    body.append("file", file);
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/feedback/images`, { method: "POST", headers: { "X-Account-Token": token }, body });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(typeof data.detail === "string" ? data.detail : "L’image n’a pas pu être envoyée.");
    return data.id;
  };
  const send = async () => {
    setError("");
    setState("busy");
    try {
      const imageIds = await Promise.all(images.map(upload));
      await apiFetch("/api/feedback", token, { method: "POST", body: JSON.stringify({ rating: rating || null, ...form, context, imageIds }), keepSession: true });
      setState("sent");
    } catch (problem) {
      setError(problem.message || "Envoi impossible.");
      setState("edit");
    }
  };
  return (
    <div className="absolute inset-0 z-50 overflow-y-auto bg-[#F7FAF8] px-4 pb-6 pt-[max(1rem,env(safe-area-inset-top))]" data-testid="feedback-dialog">
      <div className="flex items-start justify-between gap-2">
        <h2 className="font-display text-xl font-bold text-[#122118]">Envoyer un commentaire</h2>
        <button type="button" aria-label="Fermer" onClick={onClose} className="rounded-full p-1 text-[#4a5f53]" data-testid="feedback-close"><X className="h-5 w-5" /></button>
      </div>
      {state === "sent" ? (
        <div className="mt-6 rounded-2xl bg-white p-4 text-sm text-[#122118] shadow-sm" data-testid="feedback-sent">
          Merci ! Votre commentaire est bien reçu.
          <button type="button" onClick={onClose} className="mt-3 block w-full rounded-full bg-[#25D366] px-4 py-2 font-semibold text-[#0a2914]">Fermer</button>
        </div>
      ) : (
        <>
          <p className="mt-1 text-[13px] text-[#4a5f53]">Plan Étude est en bêta : votre avis façonne le produit. Dites-nous ce qui fonctionne, ce qui casse, ce que vous aimeriez.</p>
          {!token && <p className="mt-2 text-xs text-[#9a6b00]" data-testid="feedback-login">Connectez-vous pour envoyer un commentaire.</p>}
          <div className="mt-4 rounded-2xl border border-gray-100 bg-white p-3 text-center">
            <p className="text-[13px] font-semibold text-[#122118]">Comment se passe votre expérience ?</p>
            <div className="mt-2 flex justify-center gap-1.5" data-testid="feedback-stars">
              {[1, 2, 3, 4, 5].map((value) => (
                <button key={value} type="button" aria-label={`${value} étoile${value > 1 ? "s" : ""}`} onClick={() => setRating(value)} className="rounded-lg border border-gray-200 p-1.5" data-testid={`feedback-star-${value}`}>
                  <Star className={`h-6 w-6 ${value <= rating ? "fill-[#F5B301] text-[#F5B301]" : "text-[#A9B8AF]"}`} />
                </button>
              ))}
            </div>
          </div>
          {FIELDS.map(([key, label, Icon, placeholder]) => (
            <label key={key} className="mt-3 block rounded-2xl border border-gray-100 bg-white p-3">
              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#122118]"><Icon className="h-4 w-4 text-[#189346]" />{label}</span>
              <textarea rows={3} maxLength={2000} value={form[key]} onChange={(event) => setForm({ ...form, [key]: event.target.value })} placeholder={placeholder}
                className="mt-2 w-full resize-y rounded-xl border border-gray-200 bg-[#FBFCFC] px-3 py-2 text-[14px] outline-none focus:border-[#25D366]" data-testid={`feedback-${key}`} />
            </label>
          ))}
          <div className="mt-3 rounded-2xl border border-gray-100 bg-white p-3" data-testid="feedback-images">
            <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[#122118]"><ImagePlus className="h-4 w-4 text-[#189346]" />Captures d’écran ou photos ({images.length}/3)</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {images.map((image, index) => (
                <div key={image.url} className="relative h-16 w-16 overflow-hidden rounded-xl border border-gray-200" data-testid={`feedback-image-${index}`}>
                  <img src={image.url} alt="" className="h-full w-full object-cover" />
                  <button type="button" aria-label="Retirer l’image" onClick={() => removeImage(index)} className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white" data-testid={`feedback-image-remove-${index}`}><X className="h-3 w-3" /></button>
                </div>
              ))}
              {images.length < 3 && (
                <label className="flex h-16 w-16 cursor-pointer items-center justify-center rounded-xl border border-dashed border-[#25D366] text-[#189346] hover:bg-[#F1FBF5]" data-testid="feedback-image-add">
                  <ImagePlus className="h-5 w-5" />
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif" multiple className="hidden" onChange={addImages} data-testid="feedback-image-input" />
                </label>
              )}
            </div>
            <p className="mt-1.5 text-[11px] text-[#829C8D]">Jusqu’à 3 images, 10 Mo max chacune.</p>
          </div>
          {!hasText && <p className="mt-2 text-xs text-[#829C8D]" data-testid="feedback-text-required">Écrivez au moins un mot dans l’un des champs pour envoyer.</p>}
          {error && <p className="mt-2 text-xs text-[#9b3b3b]" data-testid="feedback-error">{error}</p>}
          <div className="mt-3 flex justify-end gap-2">
            <button type="button" onClick={onClose} className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-[#2d5a3d]" data-testid="feedback-cancel">Annuler</button>
            <button type="button" onClick={send} disabled={!token || state === "busy" || !hasText} className="rounded-full bg-[#25D366] px-5 py-2 text-sm font-semibold text-[#0a2914] disabled:opacity-50" data-testid="feedback-send">
              {state === "busy" ? "Envoi…" : "Envoyer"}
            </button>
          </div>
          <p className="mt-2 text-center text-[11px] text-[#A9B8AF]">Lié à votre compte pour qu’on puisse faire un suivi si besoin.</p>
        </>
      )}
    </div>
  );
};
